import { xhr } from '@/xhr/endpoints';
import { getId } from '@/assets/js/utils';
import { EventBus } from '@/assets/js/event-bus';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';

const actionButtonStatusCallbacks = actionKey => {
  return {
    begin: () => EventBus.emit('mainActionButton.status', actionKey, LoadingStatus.LOADING),
    success: () => EventBus.emit('mainActionButton.status', actionKey, LoadingStatus.NONE),
    failed: () => EventBus.emit('mainActionButton.status', actionKey, LoadingStatus.FAILED),
  };
};

// state
const state = {
  debateStatus: null,
  debateStatuses: {},
  debateNextState: null,
  collapsExpands: false,
  collapsExpand: false,
  secondInvites: false,

  /**
   * Workflow Status
   * Define mappings for action labels returned by the next_state endpoint and their correspoding event
   */
  mainActionButton: {
    EDIT_NLP_OUTLINE: {
      text: 'Outline',
      status: LoadingStatus.NONE,
      click: () => {
        EventBus.emit(
          'debate.basis.save_request_nlp_outline',
          actionButtonStatusCallbacks('EDIT_NLP_OUTLINE')
        );
      },
      tooltip: 'Extract an argumentative outline and summary sentences',
    },

    FINALIZE_HOST_BASIS: {
      text: 'Finalize & Start',
      status: LoadingStatus.NONE,
      click: () => {
        EventBus.emit(
          'debate.basis.save_and_continue',
          actionButtonStatusCallbacks('FINALIZE_HOST_BASIS')
        );
      },
      tooltip: 'Complete framing and open first round of replies',
    },

    FINALIZE_OPPOSITION_BASIS: {
      text: 'Finalize & Start',
      status: LoadingStatus.NONE,
      click: () => {
        EventBus.emit(
          'debate.basis.save_and_invite',
          actionButtonStatusCallbacks('FINALIZE_OPPOSITION_BASIS')
        );
      },
      tooltip: 'Complete framing and open first round of replies',
    },

    FINALIZE_CURATOR_BASIS: {
      text: 'Finalize & Start',
      status: LoadingStatus.NONE,
      click: () => {
        EventBus.emit(
          'debate.basis.save_and_invite',
          actionButtonStatusCallbacks('FINALIZE_OPPOSITION_BASIS')
        );
      },
      tooltip: 'Complete framing and open first round of replies',
    },
    REQUEST_ANALYTICS: {
      text: 'Discourse Analysis',
      status: LoadingStatus.NONE,
      click: () => {
        EventBus.emit(
          'workflow.discourse.analysis',
          actionButtonStatusCallbacks('REQUEST_ANALYTICS')
        );
      },
      tooltip: 'Complete framing and open first round of replies',
    },
    //  need to work on this
    SUBMIT_ANNOTATIONS: {
      text: 'Submit Annotations',
      status: LoadingStatus.NONE,
      // click: () => {
      //   EventBus.emit('workflow.team.submit', actionButtonStatusCallbacks('FINALIZE_OPPOSITION_BASIS'));
      // },
      tooltip: 'Complete framing and open first round of replies',
    },
    SUGGEST_ANNOTATIONS: {
      text: 'Suggest Annotations',
      status: LoadingStatus.NONE,
      // click: () => {
      //   EventBus.emit('workflow.team.submit', actionButtonStatusCallbacks('FINALIZE_OPPOSITION_BASIS'));
      // },
      tooltip: 'Complete framing and open first round of replies',
    },

    BEGIN_DRAFTING: {
      text: 'Annotate',
      status: LoadingStatus.NONE,
      click: async () => {
        EventBus.emit(
          'workflow.next.finalizeNLPOutline',
          actionButtonStatusCallbacks('BEGIN_DRAFTING')
        );
      },
      tooltip: 'Complete framing and open first round of replies',
    },

    CLOSE_DRAFTING: {
      text: 'Close Drafting',
      status: LoadingStatus.NONE,
      click: async () => {
        EventBus.emit(
          'workflow.team.closeDrafting',
          actionButtonStatusCallbacks('CLOSE_DRAFTING')
        );
      },
      tooltip: 'Close team drafting and begin review',
    },

    SUBMIT_DRAFT: {
      text: 'Submit Draft',
      status: LoadingStatus.NONE,
      click: async () => {
        EventBus.emit('workflow.team.submit', actionButtonStatusCallbacks('SUBMIT_DRAFT'));
      },
      tooltip: 'Submit recommendations for review',
    },

    FINALIZE_ROUND: {
      text: 'Finalize Round',
      status: LoadingStatus.NONE,
      click: () => {
        EventBus.emit('workflow.next.publish', actionButtonStatusCallbacks('FINALIZE_ROUND'));
      },
      tooltip: 'Close and publish to your Counterpart',
    },

    INVITE_OPPOSITION: {
      text: 'Finalize Round',
      status: LoadingStatus.NONE,
      click: async () => {
        EventBus.emit(
          'workflow.next.inviteOpposition',
          actionButtonStatusCallbacks('INVITE_OPPOSITION')
        );
      },
      tooltip: 'Close and publish to your Counterpart',
    },
    COPY_OPPOSITION: {
      text: 'Finalize Round',
      status: LoadingStatus.NONE,
      click: async () => {
        EventBus.emit(
          'workflow.next.copyInviteOpposition',
          actionButtonStatusCallbacks('COPY_OPPOSITION')
        );
      },
      tooltip: 'Close and publish to your Counterpart',
    },

    AWAITING_REPLIES: (round, status, permissionLevel) => {
      if (permissionLevel === PermissionLevel.COHORT) {
        if (round === 0)
          return {
            text: 'Awaiting Framing',
            tooltip: 'Awaiting first round of replies',
            isDisabled: true,
          };
        if (status === RoundStatus.FINALIZING)
          return {
            text: 'Awaiting Round Closure',
            tooltip: 'Awaiting Round Closure',
            isDisabled: true,
          };
      }
      return {
        text: 'Awaiting Replies',
        tooltip: "Awaiting your Counterpart's replies",
        isDisabled: true,
      };
    },

    LOADING_ACTIONS: () => {
      return {
        text: 'Loading Actions',
        tooltip: 'Please wait while we get your next step',
        status: LoadingStatus.LOADING,
        isDisabled: true,
      };
    },
  },
};

//
// getters
const getters = {
  debateID: state => state.debateStatus?._id,

  contentFormatIsCompose: state => state.debateStatus.contentFormat === 'Compose',
  contentFormatIsImport: state => state.debateStatus.contentFormat === 'Import',

  contentTypeIsLongForm: state => state.debateStatus.contentType === 'Long-form',
  contentTypeIsOutline: state => state.debateStatus.contentType === 'Outline',

  /**
   * Team status
   */
  hasTeamMembers: state => {
    // WRONG: need to find the current use and the corresponding team, not the latest round owner
    if (state.debateStatus.roundOwner === 'Host') {
      return state.debateStatus.team.host.hasMemmbers;
    }

    if (state.debateStatus.roundOwner === 'Opposition') {
      return state.debateStatus.team.opposition.hasMemmbers;
    }
  },

  langBotAnalysisDone: state => state.debateStatus.lbAnalysisDone,

  buttonForAction: state => action =>
    state.mainActionButton[action.toUpperCase().replace(/ /g, '_')],
};

//
// mutations
const mutations = {
  setDebateStatus: (state, value) => (state.debateStatus = value),
  setDebateStatuses: (state, value) => {
    state.debateStatuses = { ...state.debateStatuses, [value._id]: value };
  },
  setDebateNextState: (state, value) => {
    let nextState;
    if (value?.round_actions && value?.round_actions.length > 0) {
      if (value.round_actions.length === 1) {
        nextState = value.round_actions[0];
      } else {
        const nonPublishedRounds = value.round_actions.filter(
          round => round.current_status !== 'Published'
        );

        if (nonPublishedRounds.length > 0) {
          nextState = nonPublishedRounds.reduce((highest, current) => {
            return current.current_round > highest.current_round ? current : highest;
          });
        } else {
          nextState = value.round_actions[1];
        }
      }
    } else {
      nextState = value;
    }
    state.debateNextState = nextState;
  },
  setSecondInvites: (state, value) => (state.secondInvites = value),
  clearDebateStatus: state => (state.debateStatus = null),
  clearDebateNextState: state => (state.debateNextState = null),
};

//
// actions
const actions = {
  getDebateStatus: async ({ state, commit }) => {
    try {
      if (
        state.debateStatuses[state.documentData?._id] &&
        window.location.href.includes('document-details')
      ) {
        commit('setDebateStatus', state.debateStatuses[state.documentData?._id]);
        return state.debateStatuses[state.documentData?._id];
      }
      const status = await xhr.getDocumentStatus(state.documentData?._id);
      commit('setDebateStatus', status);
      commit('setDebateStatuses', status);
      if (status) {
        state.collapsExpands = true;
      }
      return status;
    } catch (err) {
      console.error(err);
    }
  },
  getPublicDebateStatus: async ({ state, commit }) => {
    try {
      const status = await xhr.getPublicDebateStatus(state.documentData?._id, state.visitor);
      commit('setDebateStatus', status);
      commit('setDebateStatuses', status);
      if (status) {
        state.collapsExpands = true;
      }
      return status;
    } catch (err) {
      console.error(err);
    }
  },

  retrievePublicDebateNextState: async ({ state, commit }) => {
    try {
      const debateSteps = await xhr.getPublicDebateNextState(state.documentData?._id);
      commit('setDebateNextState', debateSteps);
      if (debateSteps && state.collapsExpands === true) {
        state.collapsExpand = true;
      }
      return debateSteps;
    } catch (err) {
      console.error(err);
    }
  },

  retrieveDebateNextState: async ({ state, commit }) => {
    try {
      const debateSteps =
        state.documentData.discussionType === 'curate'
          ? await xhr.getDebateNextStateCurator(state.debateStatus?._id)
          : await xhr.getDebateNextState(state.debateStatus?._id);
      commit('setDebateNextState', debateSteps);
      if (debateSteps && state.collapsExpands === true) {
        state.collapsExpand = true;
      }
      return debateSteps;
    } catch (err) {
      console.error(err);
    }
  },

  continueToNextRound: async ({ state, dispatch }, { team, isOpposition = false }) => {
    try {
      const newRound = await xhr.continueToNextRound(
        state.debateStatus?._id,
        getId(team),
        getId(team?.owner),
        isOpposition
      );

      dispatch('retrieveDebateNextState');

      return newRound;
    } catch (err) {
      console.error(err);
    }
  },
  continueToNextRoundCurator: async ({ state, dispatch }, { team }) => {
    try {
      const newRound = await xhr.continueToNextRoundCurator(state.debateStatus?._id, getId(team));
      dispatch('retrieveDebateNextState');

      return newRound;
    } catch (err) {
      console.error(err);
    }
  },
};

//
// exports
export default {
  // namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
